import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import type { ProtocolItemModel } from 'shared/domain/inspection/inspectionModel';
import type { HashMap } from 'shared/types/commonView';
import { isRichTextEmpty } from 'shared/utils/richText';

import { findProtocolAnswerByChecklistAnswerId } from './findProtocolAnswerByChecklistAnswerId';
import {
  buildComplianceAnswerFormKey,
  buildLegacyComplianceAnswerFormKey,
  buildNumericAnswerFormKey,
  buildOptionSelectionAnswerFormKey,
  buildTextAnswerFormKey,
} from './formKeys';
import { hasAnswerValue } from './types';

const VALID_COMPLIANCE_RESULTS: ComplianceAnswerOption[] = [
  ComplianceAnswerOption.passed,
  ComplianceAnswerOption.notPassed,
];

export function isComplianceAnswerResultValid(
  value: ComplianceAnswerOption | undefined
): boolean {
  return (
    value !== undefined &&
    value !== ComplianceAnswerOption.unset &&
    VALID_COMPLIANCE_RESULTS.includes(value)
  );
}

export function isProtocolAnswerFilled(
  form: HashMap<unknown>,
  checklistItemId: string,
  answerId: string,
  answerType: AnswerType,
  protocolItem?: ProtocolItemModel
): boolean {
  const persistedAnswer = findProtocolAnswerByChecklistAnswerId(
    protocolItem?.answers,
    answerId
  );

  if (persistedAnswer?.isApplicable === false) {
    return true;
  }

  switch (answerType) {
    case AnswerType.Compliance: {
      const formResult =
        (form[buildComplianceAnswerFormKey(checklistItemId, answerId)] as
          | ComplianceAnswerOption
          | undefined) ??
        (form[buildLegacyComplianceAnswerFormKey(checklistItemId)] as
          | ComplianceAnswerOption
          | undefined);

      if (isComplianceAnswerResultValid(formResult)) {
        return true;
      }
      break;
    }
    case AnswerType.Numeric: {
      const value = form[buildNumericAnswerFormKey(checklistItemId, answerId)];
      if (value !== undefined && value !== null) {
        return true;
      }
      break;
    }
    case AnswerType.Text: {
      const value = form[buildTextAnswerFormKey(checklistItemId, answerId)];
      if (typeof value === 'string' && !isRichTextEmpty(value)) {
        return true;
      }
      break;
    }
    case AnswerType.OptionSelection: {
      const value = form[
        buildOptionSelectionAnswerFormKey(checklistItemId, answerId)
      ] as string[] | undefined;
      if (value?.length) {
        return true;
      }
      break;
    }
  }

  return persistedAnswer ? hasAnswerValue(persistedAnswer) : false;
}
